import { useSelector, useDispatch } from "react-redux";
import {DataGrid, gridClasses, GridToolbar} from '@mui/x-data-grid';
/*
import {
    Box,
    Button,
    Divider,
    Icon,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
*/
import {
    Box,
    Button,
    MenuItem,
    Select,
    TextField
}from "@mui/material";

import { styled } from '@mui/material/styles';
//import {stringSortComparator, phoneNumberSortComparator } from "./utilities/string";
import {useEffect, useState} from "react";
import {createUser, getUsers, resetUserPassword} from "./actions/users";
import {addUser, setUsers} from "./reducers/users";
//import {useNavigate, useParams} from "react-router-dom";
import {useParams} from "react-router-dom";
//import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {useCookies} from "react-cookie";
import jwt_decode from "jwt-decode";
//import roles, {userManagementRoles} from "./utilities/roles";
import roles from "./utilities/roles";
import {
    canResetPassword, passwordProblem, isAssignableRole,
    ASSIGNABLE_ROLE_IDS, MIN_PASSWORD_LENGTH
} from "./utilities/permissions";

const columns = [
    { field: 'id', headerName: 'ID', width: 100, editable: false },
    { field: 'username', headerName: 'Username', width: 250, editable: false, },
    { field: 'role_name', headerName: 'Role', width: 250, editable: false, },
]

const pageSizeOptions = [20];

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
    [`&`]: { height: '850px' },
    [`& .${gridClasses.row}.even`]: {
        backgroundColor: '#F8F8F8'
    },
    [`& .${gridClasses.row}.Mui-selected`]: {
        backgroundColor: '#006225', // TODO make this match Compucycle branding guidelines,
        color: 'white'
    },
    [`& .${gridClasses.row}.Mui-selected:hover`]: {
        backgroundColor: '#009f3c', // TODO make this match Compucycle branding guidelines,
        color: 'white'
    },
}));

const UserManagement = (props = {}) => {
    //const navigate = useNavigate();
    const users = useSelector(state => state.users.value )
    const auth = useSelector(st => st.auth.value )
    //const [cookies, setCookie, removeCookie] = useCookies(['access_token']);
    const [cookies] = useCookies(['access_token']);
    // P1-20: who is doing the resetting. Restored (it was commented out) because
    // canResetPassword needs the actor's role AND id -- their role to compare against
    // the target's, their id to refuse a self-reset. `auth` in redux does not carry the
    // role, so the claim is read from the token, as AssetManagement.js does.
    const decoded = jwt_decode(cookies.access_token);
    //const canManageUsers = userManagementRoles.includes(decoded.user.role);

    const dispatch = useDispatch();
    const { id } = useParams();
    const [ isLoaded, setIsLoaded ] = useState(false);

    const [ selectedUser, setSelectedUser ] = useState(null);
    const [ cleanUser, setCleanUser ] = useState(null);
    //const [setCleanUser ] = useState(null);
    const [ newUser, setNewUser ] = useState({});
    const [ dirtyFlag, setDirtyFlag ] = useState(false);
    //const [setDirtyFlag ] = useState(false);
    
    const [ showNewUserDialog, setShowNewUserDialog ] = useState(false);
    const [ creatingUser, setCreatingUser ] = useState(false);
    //const [ updatingUser, setUpdatingUser ] = useState(false);
    const [ updatingUser] = useState(false);

    // P1-21: the New User dialog collected a username and nothing else, so every
    // creation was rejected by the server for a missing password and role. These are
    // the two fields it needed, plus somewhere to show the refusal instead of logging
    // it to the console.
    const [ newUserPasswordConfirmation, setNewUserPasswordConfirmation ] = useState('');
    const [ newUserError, setNewUserError ] = useState('');

    // P1-20: the admin reset.
    const [ showResetPasswordDialog, setShowResetPasswordDialog ] = useState(false);
    const [ resetPassword, setResetPassword ] = useState('');
    const [ resetPasswordConfirmation, setResetPasswordConfirmation ] = useState('');
    const [ resettingPassword, setResettingPassword ] = useState(false);
    const [ resetError, setResetError ] = useState('');
    const [ resetDone, setResetDone ] = useState('');

    // ⚠ Lower role id means higher privilege, so this is what stops a Manager
    // resetting a System Admin's password and logging in as them. The server refuses it
    // too -- this only decides whether the control is offered. `roles[0]` is the role
    // as stored, not the pending value of the (inert) role dropdown.
    const mayResetSelected = selectedUser != null && canResetPassword({
        actorRole: decoded.user.role,
        actorId: decoded.user.id,
        targetRole: selectedUser.roles?.[0],
        targetId: selectedUser.id
    });

    const closeResetDialog = () => {
        setShowResetPasswordDialog(false);
        setResetPassword('');
        setResetPasswordConfirmation('');
        setResetError('');
    };

    const processReset = async () => {
        setResettingPassword(true);
        setResetDone('');
        const result = await resetUserPassword(auth.token, selectedUser.id, resetPassword);
        setResettingPassword(false);
        if( result?.status === "success" ) {
            setResetDone(`Password reset for ${selectedUser.username}.`);
            closeResetDialog();
        } else {
            // Includes PASSWORD_RESET_FORBIDDEN, which a stale tab can still provoke:
            // the target's role may have changed since this list was loaded.
            setResetError(result?.message || 'Could not reset the password');
        }
    };

    const resetProblem = passwordProblem(resetPassword);
    const canSubmitReset = resetProblem === null && resetPassword === resetPasswordConfirmation;

    const newUserProblem = passwordProblem(newUser.password);
    const canSubmitNewUser = String(newUser.username ?? '').trim() !== ''
        && newUserProblem === null
        && newUser.password === newUserPasswordConfirmation
        && isAssignableRole(newUser.role);

    const updateUserField = (field, value) => {
        const updatedUser = Object.assign({}, selectedUser, { [field]: value } );
        setSelectedUser(updatedUser);
        setDirtyFlag(true);
    }

    const updateNewUserField = (field, value) => {
        const updatedUser = Object.assign({}, newUser, { [field]: value } );
        setNewUser(updatedUser);
    }

    useEffect(() => {
        const fetchData = () => async (dispatch) => {
            if( users.length === 0 ) {
                const updatedUsers = await getUsers(auth.token);
                const updatedUsersData = updatedUsers.data.map((user) => {
                    return Object.assign({}, user, { role_name: roles[user.roles[0]]});
                });
                dispatch(setUsers(updatedUsersData));
            }
            setIsLoaded(true);
        }
        dispatch(fetchData());
    }, [dispatch, users, auth]);

    useEffect(() => {
        if(id !== null && id !== '') {
            setSelectedUser(users.find(c => c.id === parseInt(id)));
        }
    }, [isLoaded,id,users]);

    const rowClickListener = (
        params, // GridRowParams
        event, // MuiEvent<React.MouseEvent<HTMLElement>>
        details, // GridCallbackDetails
    ) => {
        if( selectedUser != null ) {
            if( selectedUser.id === params.row.id ) {
                setSelectedUser(null);
                setCleanUser(null);
            } else {
                setSelectedUser(params.row);
                setCleanUser(params.row);
            }
        } else {
            setSelectedUser(params.row);
            setCleanUser(params.row);
        }
    };

    return (
        <Box sx={{  height: '100vh', width: '100%' }}>
            <div style={{ paddingTop: 10}}>
                <Button style={{margin: 10}} onClick={() => setShowNewUserDialog(true)} variant="contained" color="primary">New User</Button>
            </div>
            {selectedUser == null ? null : (
                <div style={{ position: "relative"}}>
                <div style={{ "display": "flex", flexDirection: "row", padding: 20}}>
                    <div style={{display: "flex", flexDirection: "column", paddingRight: 20}}>
                    <TextField onChange={(e) => updateUserField('username', e.target.value)} style={{marginTop: 10}} id="user-name" label="User Name" variant="outlined" value={selectedUser.username} />
                        <div style={{marginTop: 10}}>
                            <Select
                                style={{marginRight: 10}}
                                id="user-role"
                                value={selectedUser.roles[0]}
                                label="Role"
                                onChange={(e) => updateUserField('role', e.target.value)}
                            >
                                {Object.entries(roles).map(([k,v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                            </Select>
                        </div>
                </div>
                    {/* P1-20: the only way to recover a locked-out account. `users` has
                        no email column, so there is no self-service reset to fall back
                        on, and before this the remedy was to disable the account and
                        create a new username.

                        ⚠ Hidden when the target is at or above the actor's role — see
                        mayResetSelected. Without that a Manager could reset any of the
                        six live System Admins and log in as them. Also hidden on your
                        own row: changing your own password belongs on the Profile page,
                        which requires the current one. */}
                    <div style={{display: "flex", flexDirection: "column", justifyContent: "flex-start", paddingTop: 10}}>
                        {mayResetSelected ? (
                            <Button onClick={() => { setResetDone(''); setShowResetPasswordDialog(true); }} variant="contained" color="primary">Reset Password</Button>
                        ) : (
                            <div style={{color: "#666", fontSize: 13, maxWidth: 260}}>
                                {selectedUser.id === decoded.user.id
                                    ? "Change your own password from your Profile."
                                    : "You may not reset the password of a user at or above your own role."}
                            </div>
                        )}
                        {resetDone !== '' ? (
                            <div style={{ marginTop: 10, padding: 5, borderRadius: 4, backgroundColor: "#006225", color: "white", textAlign: "center"}}>{resetDone}</div>
                        ) : null}
                    </div>
                </div>
                    {updatingUser ? (
                        <div style={{position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "#00000022", display: "flex", justifyContent: "center", alignItems: "center"}}>
                            <div className="lds-roller">
                                <div></div>
                                <div></div>
                                <div></div>
                                <div></div>
                                <div></div>
                                <div></div>
                                <div></div>
                                <div></div>
                            </div>
                        </div>
                    ) : null }
                </div>
            )}
                <StripedDataGrid
                    slots={{ toolbar: GridToolbar }}
                    getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                    }
                    rows={users}
                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 20,
                            },
                        },
                    }}
                    density="compact"
                    pageSizeOptions={pageSizeOptions}
                    onRowClick={rowClickListener}
                />
            {!isLoaded ? (
                <div style={{position: "fixed", top:0, bottom: 0, left: 0, right:0, backgroundColor: "#00000022", display: "flex", justifyContent: "center", alignItems: "center"}}>
                    <div className="lds-roller">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                </div>
            ) : null}
            {showNewUserDialog ? (
                <div style={{position: "fixed", top:0, bottom: 0, left: 0, right:0, backgroundColor: "#00000022", display: "flex", justifyContent: "center", alignItems: "center"}}>
                    <div style={{display: "flex", flexDirection: "column", background: "white"}}>
                        <div style={{fontSize: 20, fontWeight: "bold", padding: 20, alignSelf: "center", display: "flex"}}>New User</div>
                        {/* P1-21: Password, Confirm and Role. This dialog collected a
                            username and nothing else, so createUser posted {username}
                            and the server rejected every attempt for a missing password
                            and role — creating a user through the UI could not succeed.
                            The Role list offers exactly what the server accepts
                            (ASSIGNABLE_ROLE_IDS), not all six display roles. */}
                        <div style={{ "display": "flex", flexDirection: "row", paddingLeft: 20, paddingRight: 20, background: "white"}}>
                            <div style={{display: "flex", flexDirection: "column", paddingRight: 20}}>
                                <TextField onChange={(e) => updateNewUserField('username', e.target.value)} style={{marginTop: 10}} id="user-name" label="User Name" variant="outlined" value={newUser.username ?? ''} />
                                <TextField type="password" onChange={(e) => updateNewUserField('password', e.target.value)} style={{marginTop: 10}} id="new-user-password" label="Password" variant="outlined" value={newUser.password ?? ''} />
                                <TextField type="password" onChange={(e) => setNewUserPasswordConfirmation(e.target.value)} style={{marginTop: 10}} id="new-user-password-confirmation" label="Confirm Password" variant="outlined" value={newUserPasswordConfirmation} />
                                <Select
                                    style={{marginTop: 10}}
                                    id="new-user-role"
                                    displayEmpty
                                    value={newUser.role ?? ''}
                                    onChange={(e) => updateNewUserField('role', e.target.value)}
                                >
                                    <MenuItem value=''>Select a role…</MenuItem>
                                    {ASSIGNABLE_ROLE_IDS.map(id => <MenuItem key={id} value={id}>{roles[id]}</MenuItem>)}
                                </Select>
                                {newUser.password?.length > 0 && newUserProblem !== null ? (
                                    <div style={{ marginTop: 10, padding: 5, borderRadius: 4, backgroundColor: "#FFDDDD", color: "red", textAlign: "center"}}>Must be at least {MIN_PASSWORD_LENGTH} characters</div>
                                ) : null}
                                {newUser.password !== newUserPasswordConfirmation ? (
                                    <div style={{ marginTop: 10, padding: 5, borderRadius: 4, backgroundColor: "#FFDDDD", color: "red", textAlign: "center"}}>Passwords do not match</div>
                                ) : null}
                                {newUserError !== '' ? (
                                    <div style={{ marginTop: 10, padding: 5, borderRadius: 4, backgroundColor: "#FFDDDD", color: "red", textAlign: "center", maxWidth: 300}}>{newUserError}</div>
                                ) : null}
                            </div>
                        </div>
                        <div style={{ padding: 20, display: "flex", flexDirection: "row-reverse"}}>
                            <Button style={{margin: 10}} disabled={!canSubmitNewUser} onClick={async () => {
                                setCreatingUser(true);
                                setNewUserError('');
                                // The Select's value is a number here, but the role
                                // dropdown elsewhere in this file is keyed by
                                // Object.entries and yields strings — so coerce rather
                                // than trusting the widget.
                                const result = await createUser(auth.token,
                                    Object.assign({}, newUser, {role: Number(newUser.role)}));
                                setCreatingUser(false);
                                if( result?.status === "success") {
                                    // Build the row the way getUsers does. It used to
                                    // dispatch `newUser` plus `result.id` — but the
                                    // response carries the id under `data`, and the grid
                                    // needs `roles` and `role_name`, so the new row
                                    // arrived with no id, no role, and crashed the detail
                                    // panel on click. It also put the plaintext password
                                    // in the redux store.
                                    const created = result.data || {};
                                    const assignedRole = Number(created.role ?? newUser.role);
                                    dispatch(addUser({
                                        id: created.id,
                                        username: created.username ?? newUser.username,
                                        is_active: true,
                                        roles: [assignedRole],
                                        role_name: roles[assignedRole]
                                    }));
                                    setShowNewUserDialog(false);
                                    setNewUser({});
                                    setNewUserPasswordConfirmation('');
                                } else {
                                    // Shown instead of logged: "Error saving asset" in the
                                    // console was the only sign anything had gone wrong.
                                    setNewUserError(typeof result?.error === 'string'
                                        ? result.error
                                        : (result?.message || 'Could not create the user'));
                                }
                            }} variant="contained" color="primary">Save</Button>
                            <Button style={{margin: 10}} onClick={() => { setShowNewUserDialog(false); setNewUser({}); setNewUserPasswordConfirmation(''); setNewUserError('');}} variant="contained" color="secondary">Cancel</Button>
                            {creatingUser ? (
                                <div style={{position: "fixed", top:0, bottom: 0, left: 0, right:0, backgroundColor: "#00000022", display: "flex", justifyContent: "center", alignItems: "center"}}>
                                    <div className="lds-roller">
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                    </div>
                                </div>
                            ) : null }
                        </div>
                    </div>
                </div>
            ) : null}
            {/* P1-20: the reset dialog. Deliberately does NOT ask for the actor's own
                password — they are proving nothing about the target account, and the
                authorisation is their role. What stops abuse is canResetPassword (and
                the same check on the server), not a second password prompt. */}
            {showResetPasswordDialog && selectedUser != null ? (
                <div style={{position: "fixed", top:0, bottom: 0, left: 0, right:0, backgroundColor: "#00000022", display: "flex", justifyContent: "center", alignItems: "center"}}>
                    <div style={{display: "flex", flexDirection: "column", background: "white"}}>
                        <div style={{fontSize: 20, fontWeight: "bold", padding: 20, alignSelf: "center", display: "flex"}}>Reset Password</div>
                        <div style={{ display: "flex", flexDirection: "column", paddingLeft: 20, paddingRight: 20, background: "white"}}>
                            <div style={{maxWidth: 320}}>Set a new password for <b>{selectedUser.username}</b> ({selectedUser.role_name}). Tell them what it is — they can change it themselves from their Profile.</div>
                            <TextField type="password" onChange={(e) => setResetPassword(e.target.value)} style={{marginTop: 10}} id="reset-password" label="New Password" variant="outlined" value={resetPassword} />
                            <TextField type="password" onChange={(e) => setResetPasswordConfirmation(e.target.value)} style={{marginTop: 10}} id="reset-password-confirmation" label="Confirm New Password" variant="outlined" value={resetPasswordConfirmation} />
                            {resetPassword.length > 0 && resetProblem !== null ? (
                                <div style={{ marginTop: 10, padding: 5, borderRadius: 4, backgroundColor: "#FFDDDD", color: "red", textAlign: "center"}}>Must be at least {MIN_PASSWORD_LENGTH} characters</div>
                            ) : null}
                            {resetPassword !== resetPasswordConfirmation ? (
                                <div style={{ marginTop: 10, padding: 5, borderRadius: 4, backgroundColor: "#FFDDDD", color: "red", textAlign: "center"}}>Passwords do not match</div>
                            ) : null}
                            {resetError !== '' ? (
                                <div style={{ marginTop: 10, padding: 5, borderRadius: 4, backgroundColor: "#FFDDDD", color: "red", textAlign: "center", maxWidth: 320}}>{resetError}</div>
                            ) : null}
                        </div>
                        <div style={{ padding: 20, display: "flex", flexDirection: "row-reverse"}}>
                            <Button style={{margin: 10}} disabled={!canSubmitReset || resettingPassword} onClick={processReset} variant="contained" color="primary">Save</Button>
                            <Button style={{margin: 10}} onClick={closeResetDialog} variant="contained" color="secondary">Cancel</Button>
                        </div>
                    </div>
                </div>
            ) : null}
        </Box>
    )
}

export default UserManagement;
